import type { ComponentType, Ref } from 'react'

import {
  Calendar01Icon,
  ChampionIcon,
  Layers01Icon,
  Layers02Icon,
  LegalDocument01Icon,
  Quiz01Icon,
  Route02Icon,
  StickyNote01Icon,
  Target01Icon,
  Task01Icon,
  UserGroupIcon
} from '@hugeicons/react'
import type { HugeiconsProps } from '@hugeicons/react'

import type { EvnttzIconName } from '@/shared/ui/EvnttzIcons'
import type { ModuleIconName } from '@/shared/ui/ModuleIcon'

/** API STI names — `ObjectiveCondition::VALID_RESOURCE_TYPES` */
export const API_RESOURCE_TYPES = [
  'Journey',
  'Stage',
  'Level',
  'Challenge',
  'Mission',
  'Document',
  'Post',
  'Programming',
  'Partner',
  'Quiz'
] as const

export type ApiResourceType = (typeof API_RESOURCE_TYPES)[number]

export type NavModuleSlug =
  | 'insights'
  | 'journeys'
  | 'missions'
  | 'quizzes'
  | 'prizes'
  | 'rewards'
  | 'social-feed'
  | 'schedule'
  | 'partners'
  | 'library'
  | 'faq'

export type ModuleRegistryKey =
  | 'journeys'
  | 'missions'
  | 'quizzes'
  | 'library'
  | 'social'
  | 'schedule'
  | 'partners'
  | 'objectives'

export type ResourceIconContext = 'nav' | 'objectives'

export type HugeIconComponent = ComponentType<
  Omit<HugeiconsProps, 'ref'> & { ref?: Ref<SVGSVGElement> }
>

export type ResourceIconSpec =
  | { kind: 'evnttz'; name: EvnttzIconName }
  | { kind: 'asset'; src: string }
  | { kind: 'huge'; Icon: HugeIconComponent }

export type ResourceLabels = {
  singular: string
  plural: string
}

export type ModuleRegistryEntry = {
  navSlug?: NavModuleSlug
  moduleIcon?: ModuleIconName
  evnttzIcon: EvnttzIconName
  /** HugeIcons canônico para cards/sheets de objetivos (tile colorido). */
  objectivesIcon: HugeIconComponent
  route: string
  section: string
  sectionPrep: string
  completeVerb: string
  accentColor: string
  heroArt?: string
}

export type ResourceTypeRegistryEntry = {
  module: ModuleRegistryKey
  label: ResourceLabels
  completeVerb?: string
  objectivesIcon?: HugeIconComponent
}

export const COMPLETED_ACCENT = '#16a34a'

/** Hero full-bleed da feature Objetivos (não substitui HugeIcon nos tiles). */
export const OBJECTIVES_HERO_ART = '/objectives/hero-icon.png'

export type GrammaticalGender = 'm' | 'f'

/**
 * Os valores que uma troca de nome edita — sem as frases que derivam deles.
 *
 * Contrato da cópia da feature. Vive aqui, e não em `ResourceLabels` ou
 * `ModuleRegistryEntry`, porque esses dois são compartilhados pelas dez entradas
 * de `RESOURCE_TYPE_REGISTRY` e pelos oito módulos — um campo novo neles
 * obrigaria as outras entradas a preenchê-lo sem necessidade.
 */
export type ObjectivesFeatureCopyValues = {
  singular: string
  plural: string
  singularLower: string
  pluralLower: string
  section: string
  sectionPrep: string
  gender: GrammaticalGender
}

export type ObjectivesFeatureCopy = ObjectivesFeatureCopyValues & {
  completedLabel: (options?: { exclamation?: boolean }) => string
  pageDescription: () => string
  progressAriaLabel: () => string
  notStartedLabel: () => string
  specificResourceTail: (options: { count: number }) => string
}

/**
 * Formas presas ao gênero do nome da feature. Moram aqui, e não no ponto de
 * uso, porque é isso que mantém a troca em uma edição: quem muda `gender` muda
 * artigo, contração, particípio e demonstrativo de uma vez. Sem a chave, os
 * dois nomes que a cliente levantou na call — "Tarefas" e "Missões", ambos
 * femininos — produziriam "Missão concluído" e "Complete os missões".
 */
const GENDER_FORMS: Record<
  GrammaticalGender,
  {
    completedParticiple: string
    startedParticiple: string
    pluralArticle: string
    pluralContraction: string
    singularDemonstrative: string
  }
> = {
  m: {
    completedParticiple: 'concluído',
    startedParticiple: 'iniciado',
    pluralArticle: 'os',
    pluralContraction: 'dos',
    singularDemonstrative: 'neste'
  },
  f: {
    completedParticiple: 'concluída',
    startedParticiple: 'iniciada',
    pluralArticle: 'as',
    pluralContraction: 'das',
    singularDemonstrative: 'nesta'
  }
}

/**
 * Nome exibido da feature Objetivos — o único lugar onde ele é escrito **neste app**.
 *
 * O registro irmão da Área do Produtor é
 * `webapp/src/(productor)/DigitalProducts/Gamification/lib/objectivesLabels.js`, com o mesmo
 * contrato de sete chaves. Não há pacote compartilhado entre os dois submódulos, então **uma troca
 * de nome são dois PRs**, um por app, com uma janela em que as duas interfaces falam nomes
 * diferentes. Quem trocar aqui e parar tem metade da entrega.
 *
 * Atenção a um homônimo entre os dois: `singularDemonstrative` aqui já vem contraído com "em"
 * (`neste`/`nesta`), e lá é a forma solta (`este`/`esta`). Carregar uma frase de um lado para o
 * outro sem olhar produz "Acesse este objetivo" ou "vê neste objetivo".
 *
 * Trocar o nome é editar os sete valores abaixo, e nada mais. As frases
 * compostas são função, e não concatenação no ponto de uso, porque artigo
 * (`os`/`as`), contração (`dos`/`das`), particípio (`concluído`/`concluída`) e
 * demonstrativo (`neste`/`nesta`) precisam concordar com o nome novo — e eles
 * derivam de `gender`, que é por isso o sétimo valor e não uma informação à
 * parte: mudar o nome sem mudar o gênero junto é o bug que a chave existe para
 * impedir.
 *
 * As formas minúsculas são chave própria, e não `.toLowerCase()` do rótulo:
 * isso funciona para substantivo comum de recurso, mas não para um nome próprio
 * ou sigla que a cliente venha a escolher.
 *
 * **Só o valor das strings muda.** Identificador é endereço do recurso, não
 * rótulo de interface: `OBJECTIVES_*`, `objectivesIcon`, o diretório
 * `features/objectives/`, a chave `objectives` de `MODULE_REGISTRY` e a rota
 * `/objetivos` permanecem como estão a cada troca de nome. Renomear símbolo
 * junto é exatamente o custo que este registro existe para eliminar — e a rota
 * mudaria links já distribuídos.
 */
export const OBJECTIVES_FEATURE_COPY: ObjectivesFeatureCopy = {
  singular: 'Desafio',
  plural: 'Desafios',
  singularLower: 'desafio',
  pluralLower: 'desafios',
  section: 'Desafios',
  sectionPrep: 'a seção Desafios',
  gender: 'm',

  completedLabel({ exclamation = false } = {}) {
    const { completedParticiple } = GENDER_FORMS[OBJECTIVES_FEATURE_COPY.gender]

    return `${OBJECTIVES_FEATURE_COPY.singular} ${completedParticiple}${exclamation ? '!' : ''}`
  },

  pageDescription() {
    const { pluralArticle } = GENDER_FORMS[OBJECTIVES_FEATURE_COPY.gender]

    return `Complete ${pluralArticle} ${OBJECTIVES_FEATURE_COPY.pluralLower} e acumule moedas.`
  },

  progressAriaLabel() {
    const { pluralContraction } = GENDER_FORMS[OBJECTIVES_FEATURE_COPY.gender]

    return `Progresso geral ${pluralContraction} ${OBJECTIVES_FEATURE_COPY.pluralLower}`
  },

  /**
   * Estado de uma meta que o participante ainda não tocou.
   *
   * O particípio concorda com o **item da feature**, e não com as condições:
   * quem ainda não foi iniciado é o objetivo. A linha vizinha
   * (`"2 de 5 concluídos"`) tem outro sujeito — as condições — e por isso não
   * mora aqui nem acompanha a troca.
   */
  notStartedLabel() {
    const { startedParticiple } = GENDER_FORMS[OBJECTIVES_FEATURE_COPY.gender]

    return `Ainda não ${startedParticiple}`
  },

  /**
   * Cauda da instrução de condição por recurso específico — "…indicados neste
   * objetivo."
   *
   * Duas concordâncias diferentes na mesma frase, e é por isso que ela é função
   * daqui: o demonstrativo (`neste`/`nesta`) concorda com a **feature**, e o
   * particípio `indicado(s)` concorda com o **recurso** que a condição aponta.
   * `count` é a contagem do recurso, não da feature, e por isso flexiona só o
   * número. O gênero do particípio segue masculino porque `ResourceLabels` não
   * carrega gênero — limitação do registro de recurso, não desta chave.
   */
  specificResourceTail({ count }) {
    const { singularDemonstrative } = GENDER_FORMS[OBJECTIVES_FEATURE_COPY.gender]

    return `indicado${count === 1 ? '' : 's'} ${singularDemonstrative} ${OBJECTIVES_FEATURE_COPY.singularLower}.`
  }
}

/**
 * Deriva de `OBJECTIVES_FEATURE_COPY` por getter, e não por cópia de valor, para
 * que a leitura aconteça depois da troca — é o que mantém um lugar só.
 */
export const OBJECTIVES_FEATURE_LABELS: ResourceLabels = {
  get singular() {
    return OBJECTIVES_FEATURE_COPY.singular
  },
  get plural() {
    return OBJECTIVES_FEATURE_COPY.plural
  }
}

/** Identidade da feature Objetivos — distinta de Missões (`Task01Icon`). */
export const OBJECTIVES_FEATURE: ModuleRegistryEntry = {
  route: '/objetivos',
  get section() {
    return OBJECTIVES_FEATURE_COPY.section
  },
  get sectionPrep() {
    return OBJECTIVES_FEATURE_COPY.sectionPrep
  },
  completeVerb: 'conclua',
  accentColor: '#7c3aed',
  evnttzIcon: 'modules',
  objectivesIcon: ChampionIcon,
  heroArt: OBJECTIVES_HERO_ART
}

export const MODULE_REGISTRY: Record<ModuleRegistryKey, ModuleRegistryEntry> = {
  journeys: {
    navSlug: 'journeys',
    moduleIcon: 'explore',
    evnttzIcon: 'journeys',
    objectivesIcon: Route02Icon,
    route: '/jornadas',
    section: 'Jornadas',
    sectionPrep: 'a seção Jornadas',
    completeVerb: 'conclua',
    accentColor: '#3b82f6'
  },
  missions: {
    navSlug: 'missions',
    moduleIcon: 'goals',
    evnttzIcon: 'missions',
    objectivesIcon: Task01Icon,
    route: '/missoes',
    section: 'Missões',
    sectionPrep: 'a seção Missões',
    completeVerb: 'conclua',
    accentColor: 'var(--color-brand)'
  },
  quizzes: {
    navSlug: 'quizzes',
    moduleIcon: 'tasks',
    evnttzIcon: 'quizzes',
    objectivesIcon: Quiz01Icon,
    route: '/quizzes',
    section: 'Quizzes',
    sectionPrep: 'a seção Quizzes',
    completeVerb: 'conclua',
    accentColor: '#ff9800'
  },
  library: {
    navSlug: 'library',
    moduleIcon: 'content',
    evnttzIcon: 'library',
    objectivesIcon: LegalDocument01Icon,
    route: '/biblioteca',
    section: 'Biblioteca',
    sectionPrep: 'a Biblioteca',
    completeVerb: 'leia',
    accentColor: '#64748b'
  },
  social: {
    navSlug: 'social-feed',
    moduleIcon: 'social',
    evnttzIcon: 'social',
    objectivesIcon: StickyNote01Icon,
    route: '/feed',
    section: 'Feed Social',
    sectionPrep: 'o Feed Social',
    completeVerb: 'leia',
    accentColor: '#06b6d4'
  },
  schedule: {
    navSlug: 'schedule',
    moduleIcon: 'events',
    evnttzIcon: 'schedule',
    objectivesIcon: Calendar01Icon,
    route: '/programacao',
    section: 'Programação',
    sectionPrep: 'a seção Programação',
    completeVerb: 'participe de',
    accentColor: '#f59e0b'
  },
  partners: {
    navSlug: 'partners',
    moduleIcon: 'partnership',
    evnttzIcon: 'partners',
    // Evnttz SVG = trio de pessoas; UserGroup alinha melhor que Building.
    objectivesIcon: UserGroupIcon,
    route: '/parceiros',
    section: 'Parceiros',
    sectionPrep: 'a seção Parceiros',
    completeVerb: 'visite',
    accentColor: '#ec4899'
  },
  objectives: OBJECTIVES_FEATURE
}

export const RESOURCE_TYPE_REGISTRY: Record<ApiResourceType, ResourceTypeRegistryEntry> = {
  Journey: {
    module: 'journeys',
    label: { singular: 'Jornada', plural: 'Jornadas' }
  },
  Stage: {
    module: 'journeys',
    label: { singular: 'Etapa', plural: 'Etapas' },
    objectivesIcon: Layers01Icon
  },
  Level: {
    module: 'journeys',
    label: { singular: 'Nível', plural: 'Níveis' },
    objectivesIcon: Layers02Icon
  },
  Challenge: {
    module: 'journeys',
    label: { singular: 'Desafio', plural: 'Desafios' },
    objectivesIcon: Target01Icon
  },
  Mission: {
    module: 'missions',
    label: { singular: 'Missão', plural: 'Missões' }
  },
  Document: {
    module: 'library',
    label: { singular: 'Documento', plural: 'Documentos' }
  },
  Post: {
    module: 'social',
    label: { singular: 'Post', plural: 'Posts' }
  },
  Programming: {
    module: 'schedule',
    label: { singular: 'Programação', plural: 'Programações' }
  },
  Partner: {
    module: 'partners',
    label: { singular: 'Parceiro', plural: 'Parceiros' }
  },
  Quiz: {
    module: 'quizzes',
    label: { singular: 'Quiz', plural: 'Quizzes' }
  }
}

export type ResolvedResourceModule = ModuleRegistryEntry & {
  apiResourceType?: ApiResourceType
  label: ResourceLabels
  objectivesIcon: HugeIconComponent
}

export function isApiResourceType(value: string): value is ApiResourceType {
  return (API_RESOURCE_TYPES as readonly string[]).includes(value)
}

export function resolveResourceType(
  resourceType?: string | null
): ResolvedResourceModule {
  if (resourceType && isApiResourceType(resourceType)) {
    const typeEntry = RESOURCE_TYPE_REGISTRY[resourceType]
    const moduleEntry = MODULE_REGISTRY[typeEntry.module]

    return {
      ...moduleEntry,
      apiResourceType: resourceType,
      label: typeEntry.label,
      completeVerb: typeEntry.completeVerb ?? moduleEntry.completeVerb,
      objectivesIcon: typeEntry.objectivesIcon ?? moduleEntry.objectivesIcon
    }
  }

  /*
   * Não congelar este fallback num rótulo próprio, e não é vazamento.
   *
   * A api valida `resource_type` por inclusão em `API_RESOURCE_TYPES`, então
   * condição com tipo inválido não chega aqui: o caminho alcançável é objetivo
   * **sem condição nenhuma**, e ali quem está sendo nomeado é a própria feature
   * — "Ir para {feature}", "Acesse a seção {feature}". Congelar produziria o
   * nome antigo dentro da seção com o nome novo.
   *
   * Com tipo conhecido o oposto já vale de graça: o espalhamento acima é de
   * `MODULE_REGISTRY[typeEntry.module]`, e nenhum dos dez tipos mapeia para o
   * módulo `objectives` — substantivo, seção e verbo são os do recurso.
   */
  return {
    ...OBJECTIVES_FEATURE,
    label: OBJECTIVES_FEATURE_LABELS
  }
}

export function getResourceIconSpec(
  resourceType: string | null | undefined,
  context: ResourceIconContext
): ResourceIconSpec {
  const resolved = resolveResourceType(resourceType)

  if (context === 'nav') {
    return { kind: 'evnttz', name: resolved.evnttzIcon }
  }

  return { kind: 'huge', Icon: resolved.objectivesIcon }
}

export function getResourceTypeLabel(
  resourceType: string,
  form: 'singular' | 'plural' = 'singular'
): string {
  return resolveResourceType(resourceType).label[form]
}

export function getPrimaryResourceType(
  conditions?: Array<{ resource_type: string }>
): string | undefined {
  return conditions?.[0]?.resource_type
}

export type ProgressUnitLabels = {
  singular: string
  plural: string
}

/**
 * Unidade de contagem na barra de escada — o que o número mede.
 * Diferente do nome do recurso quando a métrica tem vocabulário próprio
 * (ex.: parceiros → check-ins).
 */
const RESOURCE_PROGRESS_UNITS: Partial<Record<ApiResourceType, ProgressUnitLabels>> = {
  Partner: { singular: 'check-in', plural: 'check-ins' },
  Mission: { singular: 'missão', plural: 'missões' },
  Document: { singular: 'leitura', plural: 'leituras' },
  Programming: { singular: 'participação', plural: 'participações' },
  Quiz: { singular: 'quiz', plural: 'quizzes' },
  Journey: { singular: 'jornada', plural: 'jornadas' },
  Stage: { singular: 'etapa', plural: 'etapas' },
  Level: { singular: 'nível', plural: 'níveis' },
  Challenge: { singular: 'desafio', plural: 'desafios' },
  Post: { singular: 'post', plural: 'posts' }
}

const LIKE_PROGRESS_UNITS: ProgressUnitLabels = {
  singular: 'curtida',
  plural: 'curtidas'
}

export function getProgressUnitLabels(
  condition?: { resource_type?: string; action_type?: string } | null
): ProgressUnitLabels {
  if (condition?.action_type === 'like') return LIKE_PROGRESS_UNITS

  if (condition?.resource_type && isApiResourceType(condition.resource_type)) {
    const custom = RESOURCE_PROGRESS_UNITS[condition.resource_type]
    if (custom) return custom

    const resolved = resolveResourceType(condition.resource_type)
    return {
      singular: resolved.label.singular.toLowerCase(),
      plural: resolved.label.plural.toLowerCase()
    }
  }

  /*
   * Sem `resource_type` quem está sendo contado é a própria feature — uma
   * escada cujo marco de amostra não tem condição renderiza "10 de 75
   * {feature}". Por isso a unidade sai do registro, e não de um literal: o
   * `.toLowerCase()` do ramo acima minúscula o substantivo do **recurso**, que
   * é outro vocabulário e não acompanha a troca de nome da feature.
   */
  return {
    singular: OBJECTIVES_FEATURE_COPY.singularLower,
    plural: OBJECTIVES_FEATURE_COPY.pluralLower
  }
}

/** Unidade pluralizada para a contagem exibida (ex.: "75 check-ins"). */
export function formatProgressUnit(
  condition?: { resource_type?: string; action_type?: string } | null,
  count: number
): string {
  const labels = getProgressUnitLabels(condition)
  return count === 1 ? labels.singular : labels.plural
}
